import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Panel, Pill, Loading, ErrorNote, Empty, Meter, formatDate } from '../components/ui';
import { useAuth, useRight } from '../lib/auth';
import { LANGUAGES, canApproveTranslation, type LanguageCode } from '@inbrief/shared';

interface Check { kind: string; label: string; value: string; present: boolean }

interface TranslationRow {
  id: string; language_code: LanguageCode; status: string; stale: boolean;
  critical_check_passed: boolean; critical_checks: Check[];
  service_name: string; format: string; version_number: number;
  created_at: string; review_count: string; communication_version_id: string;
}

interface Communication {
  id: string; service_name: string; format: string;
  version_id: string | null; status: string;
}

interface TranslationDetailData {
  translation: { body: string; source_body: string; language_code: LanguageCode };
  reviews: {
    action: string; reviewer_name: string; competence_basis: string;
    note: string | null; created_at: string;
  }[];
}

/**
 * The translation next to the English it came from.
 *
 * Right-to-left languages need `dir="rtl"` on the target pane only, and any
 * Latin text inside it — a telephone number, the practice name — has to be
 * isolated with <bdi> or the bidirectional algorithm reorders it. A phone
 * number rendered backwards in an Urdu letter is exactly the kind of error
 * the critical-value check cannot catch, because the characters are all there.
 */
function TranslationDetail({ id, rtl }: { id: string; rtl: boolean }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['translation', id],
    queryFn: () => api.get<TranslationDetailData>(`/api/translations/${id}`),
  });

  if (isLoading) return <Loading what="Loading the translation" />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  return (
    <>
      <div className="translation-panes">
        <div>
          <div className="preview-label">Approved English</div>
          <pre className="preview-area">{data.translation.source_body}</pre>
        </div>
        <div>
          <div className="preview-label">Translation</div>
          <pre
            className="preview-area"
            dir={rtl ? 'rtl' : 'ltr'}
            lang={data.translation.language_code}
          >
            {rtl ? isolateLatin(data.translation.body) : data.translation.body}
          </pre>
        </div>
      </div>

      {data.reviews.length ? (
        <div className="timeline" style={{ marginTop: 12 }}>
          {data.reviews.map((r, i) => (
            <div className="tl-item" key={i}>
              <div className="tl-when">{formatDate(r.created_at)}</div>
              <div className="tl-what">
                {r.action === 'corrected' ? 'Corrected' : r.action === 'rejected' ? 'Rejected' : 'Reviewed'}
                {' by '}{r.reviewer_name}
              </div>
              {/* Recorded because "how do you know they could check it?" is the
                  first question asked after a translation incident. */}
              <div className="tl-who">{r.competence_basis}</div>
            </div>
          ))}
        </div>
      ) : (
        <p className="field-hint">
          Nobody has checked this translation yet. It cannot be approved until someone does.
        </p>
      )}
    </>
  );
}

/**
 * Wraps runs of Latin characters and digits in <bdi> so the bidirectional
 * algorithm leaves them alone inside right-to-left text.
 */
function isolateLatin(text: string) {
  const parts = text.split(/([A-Za-z0-9][A-Za-z0-9 .,:@\/+-]*[A-Za-z0-9]|[A-Za-z0-9])/g);
  return parts.map((part, i) =>
    /^[A-Za-z0-9]/.test(part) ? <bdi key={i}>{part}</bdi> : part,
  );
}

export function Translate() {
  const { user } = useAuth();
  const canDraft = useRight('comms');
  const canApprove = user ? canApproveTranslation(user.role) : false;
  const queryClient = useQueryClient();

  const [versionId, setVersionId] = useState('');
  const [language, setLanguage] = useState<LanguageCode>('pl');

  const translations = useQuery({
    queryKey: ['translations'],
    queryFn: () => api.get<{
      translations: TranslationRow[];
      allowance: { used: number; limit: number; practiceCount: number; planId: string };
    }>('/api/translations'),
  });

  const communications = useQuery({
    queryKey: ['communications'],
    queryFn: () => api.get<{ communications: Communication[] }>('/api/communications'),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['translations'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const create = useMutation({
    mutationFn: () => api.post('/api/translations', {
      communicationVersionId: versionId, languageCode: language,
    }),
    onSuccess: invalidate,
  });
  const review = useMutation({
    mutationFn: ({ id, reviewerName, competenceBasis }: {
      id: string; reviewerName: string; competenceBasis: string;
    }) => api.post(`/api/translations/${id}/review`, { action: 'reviewed', reviewerName, competenceBasis }),
    onSuccess: invalidate,
  });
  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/api/translations/${id}/approve`),
    onSuccess: invalidate,
  });

  const approved = (communications.data?.communications ?? []).filter((c) => c.status === 'approved');
  const allowance = translations.data?.allowance;

  return (
    <>
      <PageHeader
        title="Translation"
        description="Translations of approved patient communications. Every one is checked by a person before it can be approved — machine translation reaches no patient unreviewed."
      />

      <div className="note-box">
        Dates, times, telephone numbers and your practice name are checked automatically to confirm
        they survived translation unchanged. Weekday and month names <em>are</em> translated — a
        Polish letter should not say “Tuesday”.
      </div>

      {allowance ? (
        <Panel title="Your allowance" icon="◷">
          <div className="kv">
            <span className="fact-label">Used this year</span>
            <span className="fact-value">
              {allowance.used.toLocaleString('en-GB')} of {allowance.limit.toLocaleString('en-GB')}
            </span>
          </div>
          <Meter used={allowance.used} limit={allowance.limit} />
          <p className="field-hint">
            {allowance.planId === 'complete' ? 240 : 60} translations per practice per year,
            across {allowance.practiceCount} practices.
          </p>
        </Panel>
      ) : null}

      {canDraft ? (
        <Panel title="New translation" icon="+">
          {approved.length === 0 ? (
            <Empty>
              No approved communications yet. Only approved English is translated, so the two cannot
              drift apart before anyone has agreed either.
            </Empty>
          ) : (
            <div className="ask-row">
              <div className="field">
                <label className="field-label" htmlFor="trSource">Approved communication</label>
                <select
                  id="trSource"
                  value={versionId || approved[0]?.version_id || ''}
                  onChange={(e) => setVersionId(e.target.value)}
                >
                  {approved.map((c) => (
                    <option key={c.version_id!} value={c.version_id!}>
                      {c.service_name} — {c.format}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="field-label" htmlFor="trLang">Language</label>
                <select
                  id="trLang"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as LanguageCode)}
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>{l.name} — {l.native}</option>
                  ))}
                </select>
              </div>
              <button
                className="btn btn-primary"
                disabled={create.isPending}
                onClick={() => {
                  if (!versionId) setVersionId(approved[0]!.version_id!);
                  create.mutate();
                }}
              >
                {create.isPending ? 'Translating…' : 'Translate'}
              </button>
            </div>
          )}
          <ErrorNote error={create.error} />
        </Panel>
      ) : null}

      {translations.isLoading ? <Loading what="Loading translations" /> : null}
      <ErrorNote error={translations.error} />

      {translations.data?.translations.length === 0 ? (
        <Empty>No translations yet.</Empty>
      ) : null}

      {translations.data?.translations.map((t) => {
        const lang = LANGUAGES.find((l) => l.code === t.language_code)!;
        const failed = t.critical_checks.filter((c) => !c.present);
        return (
          <Panel
            key={t.id}
            title={`${t.service_name} — ${lang.name} (${lang.native})`}
            icon="⇄"
            actions={
              <span className="row-gap">
                {t.stale ? <Pill tone="superseded">Source changed</Pill> : null}
                <Pill tone={
                  t.status === 'approved' ? 'approved'
                    : t.status === 'blocked' ? 'rejected'
                    : t.status === 'reviewed' ? 'review' : 'draft'
                }>
                  {t.status}
                </Pill>
              </span>
            }
          >
            {t.stale ? (
              <div className="warn-box">
                The English source has been approved again since this was translated. It must be
                regenerated and re-checked before it can be used.
              </div>
            ) : null}

            {failed.length ? (
              <div className="warn-box">
                <strong>Check failed.</strong> These values did not survive translation unchanged:
                <ul>
                  {failed.map((c, i) => <li key={i}>{c.label}: {c.value}</li>)}
                </ul>
                This translation cannot be approved until that is corrected.
              </div>
            ) : (
              <div className="verified">
                All {t.critical_checks.length} critical values carried across unchanged.
              </div>
            )}

            {/*
              The reviewer has to be able to read what they are certifying,
              against the English it came from. Without this the human review
              gate is a button someone presses without seeing the text.
            */}
            <TranslationDetail id={t.id} rtl={lang.rtl} />

            <details className="value-check">
              <summary>Every value checked ({t.critical_checks.length})</summary>
              <div className="table-wrap">
                <table className="dt">
                  <caption className="visually-hidden">Critical value checks</caption>
                  <thead>
                    <tr><th scope="col">What</th><th scope="col">Value</th><th scope="col">Survived</th></tr>
                  </thead>
                  <tbody>
                    {t.critical_checks.map((c, i) => (
                      <tr key={i}>
                        <th scope="row">{c.label}</th>
                        <td><bdi>{c.value}</bdi></td>
                        <td>{c.present ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            <div className="row-gap" style={{ marginTop: 12 }}>
              {canDraft && t.status === 'draft' ? (
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => {
                    const reviewerName = window.prompt('Who checked this translation?');
                    if (!reviewerName?.trim()) return;
                    const competenceBasis = window.prompt(
                      'How was their language competence established? (recorded permanently)',
                    );
                    if (!competenceBasis?.trim()) return;
                    review.mutate({ id: t.id, reviewerName, competenceBasis });
                  }}
                >
                  Record a review
                </button>
              ) : null}
              {canApprove && t.status === 'reviewed' && !t.stale ? (
                <button className="btn btn-primary btn-small" onClick={() => approve.mutate(t.id)}>
                  Approve
                </button>
              ) : null}
              <span className="field-hint">
                {Number(t.review_count)} review{Number(t.review_count) === 1 ? '' : 's'} recorded ·
                created {formatDate(t.created_at)}
              </span>
            </div>
            <ErrorNote error={review.error ?? approve.error} />
          </Panel>
        );
      })}
    </>
  );
}
